import * as Sentry from '@sentry/react';
import i18n from '../i18n';
import config from '../config';
import { useUserStore } from '../stores/userStore';
// import { tokenRefreshService } from './firebaseAuth'; // Temporarily disabled
import { ApiError } from '../types/api';

// Backend API base URL
const API_BASE_URL = config.apiUrl;

export const getToken = () => {
  const tokenFromStore = useUserStore.getState().getToken();
  if (tokenFromStore) return tokenFromStore;

  // Fallback to direct localStorage check if store isn't hydrated correctly 
  // or if token is stored under a different key
  return localStorage.getItem('userToken') || localStorage.getItem('token');
};

const buildJsonHeaders = (withAuth: boolean): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (withAuth) {
    const token = getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

const authorizationHeader = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const token = getToken();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

let refreshPromise: Promise<boolean> | null = null;
let isRefreshing = false;

const waitForTokenRefresh = async (
  store: ReturnType<typeof useUserStore.getState>
): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = store.ensureValidToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

// Helper function to handle token refresh and retry requests
const handleTokenRefresh = async (
  originalRequest: () => Promise<Response>,
  url: string,
  method: string
): Promise<Response> => {
  const MAX_RETRIES = 2;
  const BASE_DELAY = 1000; // 1 second
  const userStore = useUserStore.getState();
  let hasRefreshedToken = false;

  // Add breadcrumb for initial request
  if (process.env.REACT_APP_SENTRY_DSN && url) {
    Sentry.addBreadcrumb({
      category: 'http',
      message: `API request: ${method || 'GET'} ${url}`,
      level: 'info',
      data: {
        url,
        method: method || 'GET',
        attempt: 1,
      },
    });
  }

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      // Attempt request with current token
      const response = await originalRequest();

      // Add breadcrumb for successful request
      if (process.env.REACT_APP_SENTRY_DSN && url) {
        Sentry.addBreadcrumb({
          category: 'http',
          message: `API request successful: ${method || 'GET'} ${url}`,
          level: 'info',
          data: {
            url,
            method: method || 'GET',
            status: response.status,
            attempt,
          },
        });
      }

      return response;
    } catch (error: unknown) {
      const apiError = error as ApiError;

      // Add breadcrumb for failed request
      if (process.env.REACT_APP_SENTRY_DSN && url) {
        Sentry.addBreadcrumb({
          category: 'http',
          message: `API request failed: ${method || 'GET'} ${url}`,
          level: 'error',
          data: {
            url,
            method: method || 'GET',
            status: apiError.status,
            attempt,
            maxRetries: MAX_RETRIES,
          },
        });
      }

      if (apiError.status === 401) {
        // Only attempt token refresh once per request to prevent infinite loops
        if (hasRefreshedToken || isRefreshing) {
          if (process.env.REACT_APP_SENTRY_DSN) {
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Token already refreshed, clearing auth state',
              level: 'error',
              data: { attempt, url },
            });
          }
          userStore.clearUser();
          throw new Error(i18n.t('api_errors.auth_expired'));
        }

        // Add breadcrumb for token refresh attempt
        if (process.env.REACT_APP_SENTRY_DSN) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Token refresh triggered due to 401',
            level: 'info',
            data: {
              attempt,
              url,
            },
          });
        }

        isRefreshing = true;
        let refreshed = false;
        try {
          refreshed = await waitForTokenRefresh(userStore);
        } finally {
          isRefreshing = false;
        }
        hasRefreshedToken = true;

        if (refreshed) {
          // Add breadcrumb for successful token refresh
          if (process.env.REACT_APP_SENTRY_DSN) {
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Token refresh successful, retrying request',
              level: 'info',
              data: {
                attempt,
                url,
              },
            });
          }
          // Retry this attempt with the new token
          attempt--;
          continue;
        }

        // Refresh failed - clear auth state
        if (process.env.REACT_APP_SENTRY_DSN) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Token refresh failed, clearing auth state',
            level: 'error',
            data: {
              attempt,
              url,
            },
          });
        }
        userStore.clearUser();
        throw new Error(i18n.t('api_errors.auth_expired'));
      }

      if (apiError.status === 408 && attempt < MAX_RETRIES) {
        const delay = BASE_DELAY * Math.pow(2, attempt - 1);

        // Add breadcrumb for timeout retry
        if (process.env.REACT_APP_SENTRY_DSN && url) {
          Sentry.addBreadcrumb({
            category: 'http',
            message: `Request timeout, retrying after ${delay}ms`,
            level: 'warning',
            data: {
              url,
              method: method || 'GET',
              attempt,
              delay,
              maxRetries: MAX_RETRIES,
            },
          });
        }

        console.warn(
          `Request timeout (attempt ${attempt}/${MAX_RETRIES}), retrying...`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      throw error;
    }
  }

  // Add breadcrumb for final failure
  if (process.env.REACT_APP_SENTRY_DSN && url) {
    Sentry.addBreadcrumb({
      category: 'http',
      message: `API request failed after ${MAX_RETRIES} retries`,
      level: 'error',
      data: {
        url,
        method: method || 'GET',
        maxRetries: MAX_RETRIES,
      },
    });
  }

  throw new Error('Request failed after multiple retry attempts');
};

// Headers without authentication for registration/login
const publicHeaders = () => ({
  'Content-Type': 'application/json',
});

export async function apiGet<T>(url: string): Promise<T> {
  const makeRequest = async (): Promise<Response> => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      headers: buildJsonHeaders(true),
      credentials: 'include',
    });
    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = 'Request failed';
      let errorData: any = undefined;
      try {
        errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || 'Request failed';
      } catch {
        errorMessage = errorText || 'Request failed';
      }
      const error = new Error(errorMessage) as ApiError;
      error.status = res.status;
      error.data = errorData;
      throw error;
    }
    return res;
  };

  const res = await handleTokenRefresh(makeRequest, url, 'GET');
  return res.json();
}

interface ApiPostOptions {
  skipAuth?: boolean;
  headers?: Record<string, string>;
}

export async function apiPost<T>(
  url: string,
  body: any,
  options: ApiPostOptions = {}
): Promise<T> {
  const makeRequest = async (): Promise<Response> => {
    const { skipAuth = false, headers = {} } = options;
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        ...buildJsonHeaders(!skipAuth),
        ...headers,
      },
      body: JSON.stringify(body),
      credentials: 'include',
    });
    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = 'Request failed';
      let errorData: any = undefined;
      try {
        errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || 'Request failed';
      } catch {
        errorMessage = errorText || 'Request failed';
      }
      const error = new Error(errorMessage) as ApiError;
      error.status = res.status;
      error.data = errorData;
      throw error;
    }
    return res;
  };

  const res = await handleTokenRefresh(makeRequest, url, 'POST');
  const jsonData = await res.json();
  return jsonData;
}

export async function apiPostFormData<T>(
  url: string,
  formData: FormData
): Promise<T> {
  const makeRequest = async (): Promise<Response> => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: authorizationHeader(),
      body: formData,
      credentials: 'include',
    });
    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = 'Request failed';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || 'Request failed';
      } catch {
        errorMessage = errorText || 'Request failed';
      }
      const error = new Error(errorMessage) as ApiError;
      error.status = res.status;
      throw error;
    }
    return res;
  };

  const res = await handleTokenRefresh(makeRequest, url, 'POST');
  return res.json();
}

// Public POST for registration/login (no auth required)
export async function apiPostPublic<T>(url: string, body: any): Promise<T> {
  // Add breadcrumb for public API request
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.addBreadcrumb({
      category: 'http',
      message: `Public API request: POST ${url}`,
      level: 'info',
      data: {
        url,
        method: 'POST',
        endpoint: url,
      },
    });
  }

  const res = await fetch(`${API_BASE_URL}${url}`, {
    method: 'POST',
    headers: publicHeaders(),
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!res.ok) {
    const errorText = await res.text();
    let errorMessage = 'Request failed';

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorData.error || 'Request failed';
    } catch {
      errorMessage = errorText || 'Request failed';
    }

    // Add breadcrumb for failed public API request
    if (process.env.REACT_APP_SENTRY_DSN) {
      Sentry.addBreadcrumb({
        category: 'http',
        message: `Public API request failed: POST ${url}`,
        level: 'error',
        data: {
          url,
          method: 'POST',
          status: res.status,
          error: errorMessage,
        },
      });
    }

    throw new Error(errorMessage);
  }

  // Add breadcrumb for successful public API request
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.addBreadcrumb({
      category: 'http',
      message: `Public API request successful: POST ${url}`,
      level: 'info',
      data: {
        url,
        method: 'POST',
        status: res.status,
      },
    });
  }

  return res.json();
}

export async function apiPatch<T>(url: string, body: any): Promise<T> {
  const makeRequest = async (): Promise<Response> => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PATCH',
      headers: buildJsonHeaders(true),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = 'Request failed';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || 'Request failed';
      } catch {
        errorMessage = errorText || 'Request failed';
      }
      const error = new Error(errorMessage) as ApiError;
      error.status = res.status;
      throw error;
    }
    return res;
  };

  const res = await handleTokenRefresh(makeRequest, url, 'PATCH');
  return res.json();
}

export async function apiPut<T>(url: string, body: any): Promise<T> {
  const makeRequest = async (): Promise<Response> => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: buildJsonHeaders(true),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = 'Request failed';
      let errorData: any = undefined;
      try {
        errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || 'Request failed';
      } catch {
        errorMessage = errorText || 'Request failed';
      }
      const error = new Error(errorMessage) as ApiError;
      error.status = res.status;
      error.data = errorData;
      throw error;
    }
    return res;
  };

  const res = await handleTokenRefresh(makeRequest, url, 'PUT');
  return res.json();
}

export async function apiPutFormData<T>(
  url: string,
  formData: FormData
): Promise<T> {
  const makeRequest = async (): Promise<Response> => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: authorizationHeader(),
      body: formData,
      credentials: 'include',
    });
    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = 'Request failed';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || 'Request failed';
      } catch {
        errorMessage = errorText || 'Request failed';
      }
      const error = new Error(errorMessage) as ApiError;
      error.status = res.status;
      throw error;
    }
    return res;
  };

  const res = await handleTokenRefresh(makeRequest, url, 'PUT');
  return res.json();
}

export async function apiDelete<T>(url: string): Promise<T> {
  const makeRequest = async (): Promise<Response> => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: 'DELETE',
      headers: buildJsonHeaders(true),
      credentials: 'include',
    });
    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = 'Request failed';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || 'Request failed';
      } catch {
        errorMessage = errorText || 'Request failed';
      }
      const error = new Error(errorMessage) as ApiError;
      error.status = res.status;
      throw error;
    }
    return res;
  };

  const res = await handleTokenRefresh(makeRequest, url, 'DELETE');
  return res.json();
}

// Reset password function (public endpoint)
export async function resetPassword(
  email: string
): Promise<{ success: boolean; message: string }> {
  return apiPostPublic<{ success: boolean; message: string }>(
    '/api/auth/reset-password',
    { email }
  );
}

// Change email functions (authenticated endpoints)
export async function initiateEmailChange(
  newEmail: string,
  password: string
): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(
    '/api/auth/change-email',
    { newEmail, password }
  );
}

export async function verifyEmailChange(
  verificationToken: string
): Promise<{ success: boolean; message: string }> {
  return apiPost<{ success: boolean; message: string }>(
    '/api/auth/verify-email-change',
    { verificationToken }
  );
}

// Ask a question — returns answer + official sources
export async function askImmigrationQuestion(
  question: string
): Promise<{ success: boolean; answer?: string; sources?: { title: string; url: string }[]; error?: string }> {
  return apiPost<{ success: boolean; answer?: string; sources?: { title: string; url: string }[]; error?: string }>(
    '/api/gpt/ask-immigration',
    { question }
  );
}

// Submit thumbs up/down feedback on an AI answer
export async function submitAiFeedback(
  question: string,
  helpful: boolean,
  answer?: string
): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>(
    '/api/gpt/feedback',
    { question, helpful, answer }
  );
}

// AI plan (legacy: just AI question count for /what-to-know)
export async function getMyPlan(): Promise<{ success: boolean; plan: 'free' | 'pro'; is_pro?: boolean; questions_used_today: number }> {
  return apiGet<{ success: boolean; plan: 'free' | 'pro'; is_pro?: boolean; questions_used_today: number }>('/api/gpt/my-plan');
}

// Free/Pro plan snapshot — plan, subscription state, weekly usage.
export interface PlanSnapshot {
  plan: 'free' | 'pro';
  subscriptionActive: boolean;
  isPro: boolean;
  reviews_access?: boolean;
  reviews_access_purchased_at?: string | null;
  limits: {
    helpRequestsPerWeek: number;
    offersPerWeek: number;
    hangoutsPerWeek: number;
    newConversationsPerWeek: number;
    photoUploadsPerWeek: number;
    aiQuestionsPerDay: number;
  };
  usage: {
    helpRequestsThisWeek: number;
    offersThisWeek: number;
    hangoutsThisWeek: number;
    newConversationsThisWeek: number;
    photoUploadsThisWeek: number;
  };
}

export async function getPlanMe(): Promise<PlanSnapshot> {
  const res = await apiGet<{ success: boolean; data: PlanSnapshot }>('/api/plan/me');
  return res.data;
}

// Saved answers
export async function saveAnswer(
  question: string,
  answer: string,
  sources: { title: string; url: string }[]
): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('/api/saved-answers', { question, answer, sources });
}

export async function unsaveAnswer(
  question: string
): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('/api/saved-answers/unsave', { question });
}

export async function getSavedAnswers(): Promise<{
  success: boolean;
  saved: { id: number; question: string; answer: string; sources: { title: string; url: string }[]; created_at: string }[];
}> {
  return apiGet<{ success: boolean; saved: any[] }>('/api/saved-answers');
}

// ── Stripe billing ────────────────────────────────────────────────────────────

/**
 * Creates a Stripe Checkout session and returns the redirect URL.
 * interval: 'month' = $7.99/mo | 'year' = $59.99/yr
 */
export async function createCheckoutSession(
  interval: 'month' | 'year' = 'month'
): Promise<{ success: boolean; url?: string; message?: string }> {
  return apiPost<{ success: boolean; url?: string; message?: string }>(
    '/api/stripe/create-checkout-session',
    { interval }
  );
}

/**
 * Reviews Pass — $4.99 one-time. Unlocks full employer review text only.
 */
export async function createReviewsPassCheckout(): Promise<{ success: boolean; url?: string; message?: string }> {
  return apiPost<{ success: boolean; url?: string; message?: string }>(
    '/api/stripe/create-reviews-pass-checkout',
    {}
  );
}

/**
 * Season Pass — $14.99 for 6 months. Unlocks everything (same as Pro).
 */
export async function createSeasonPassCheckout(): Promise<{ success: boolean; url?: string; message?: string }> {
  return apiPost<{ success: boolean; url?: string; message?: string }>(
    '/api/stripe/create-season-pass-checkout',
    {}
  );
}

/**
 * Opens the Stripe Customer Portal so the user can manage or cancel their
 * subscription. Returns a redirect URL.
 */
export async function getStripePortalUrl(): Promise<{ success: boolean; url?: string; message?: string }> {
  return apiPost<{ success: boolean; url?: string; message?: string }>(
    '/api/stripe/portal',
    {}
  );
}
